import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useRef,
  useState
} from "react";
import { Button, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import MaterialTable from "@material-table/core";

import GetAppIcon from "@material-ui/icons/GetApp";
import MoneyIcon from "@material-ui/icons/MonetizationOn";
import PaymentIcon from "@material-ui/icons/Payment";

import useStyles from "./styles";

import { get } from "../../api/api";
import { Authenticated } from "../../models/User";
import DateRangePicker from "../../components/inputs/daterangepickerv2";
import { Currency } from "../../components/misc/Currency";
import { UserContext } from "../../UserProvider";
import MobilePOSReceiptItems from "./MobilePOSReceiptItems";
import utc from "dayjs/plugin/utc";
import { useI18n } from "../../hooks/useI18n";

dayjs.extend(utc);

const fetchReceipts = async (
  startDate: string | null,
  endDate: string | null
) => {
  if (!startDate || !endDate) {
    return;
  }
  const response = await get(
    `/v1/dashboard/mobile-pos?periodFrom=${encodeURIComponent(startDate)}
        &periodTo=${encodeURIComponent(endDate)}`
  );
  const data = await response.json();
  return data;
};

export const MobilePOSReceipts: FunctionComponent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallDown = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const handleDateRangeChange = ([start, end]: [
    Dayjs | null,
    Dayjs | null
  ]) => {
    setStartDate(start);
    setEndDate(end);
  };

  const formattedStartDate = startDate
    ? startDate.format("YYYY-MM-DDTHH:mm:ssZ")
    : null;
  const formattedEndDate = endDate
    ? endDate.format("YYYY-MM-DDTHH:mm:ssZ")
    : null;

  const { data, isLoading, isError } = useQuery(
    ["receipts", formattedStartDate, formattedEndDate],
    () => fetchReceipts(formattedStartDate, formattedEndDate),
    {
      staleTime: Infinity,
      enabled: !!startDate && !!endDate
    }
  );

  const user = useContext(UserContext) as Authenticated;

  return (
    <div>
      <div className={classes.dialogHeader}>
        <Typography
          variant="button"
          component="span"
          style={{ fontWeight: 500, marginRight: 16 }}
        >
          {t("common.receiptsForPeriod")}
        </Typography>
        <DateRangePicker
          onDateRangeChange={handleDateRangeChange}
          maxDate={dayjs()}
        />
      </div>

      <MaterialTable
        style={{
          marginBottom: 20,
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          borderRadius: 8
        }}
        isLoading={isLoading}
        title=""
        columns={[
          {
            title: t("common.tableHeaders.receiptId"),
            field: "id",
            cellStyle: {
              padding: isSmallDown ? "8px" : "12px"
            },
            render: rowData => {
              const receiptTypeLabel = t(`common.receiptTypes.${rowData.type}`);
              return (
                <div>
                  <div>{rowData.id}</div>
                  <div
                    style={{
                      fontSize: "0.85em",
                      color: "#666",
                      marginTop: "2px"
                    }}
                  >
                    {t("common.receiptDetails.sequenceNumber")}{" "}
                    {rowData.sequenceNumber}
                  </div>
                  <div
                    style={{
                      fontSize: "0.85em",
                      color: "#666",
                      marginTop: "2px"
                    }}
                  >
                    {t("common.receiptDetails.type")} {receiptTypeLabel}
                  </div>
                </div>
              );
            }
          },
          {
            title: t("common.tableHeaders.cashier"),
            field: "cashierName",
            cellStyle: { padding: isSmallDown ? "8px" : "12px" }
          },

          {
            title: t("common.tableHeaders.created"),
            field: "created",
            cellStyle: { padding: isSmallDown ? "8px" : "12px" },
            render: rowData => dayjs(rowData.created).format("YYYY-MM-DD HH:mm")
          },
          {
            title: t("common.tableHeaders.paymentProvider"),
            field: "paymentProvider.name",
            cellStyle: { padding: isSmallDown ? "8px" : "12px" },
            render: (rowData: any) => {
              const providerName = rowData.paymentProvider?.name?.toLowerCase();

              debugger;
              const providerMap = {
                cash: {
                  icon: (
                    <MoneyIcon
                      style={{
                        fontSize: 16,
                        marginRight: 4,
                        verticalAlign: "middle"
                      }}
                    />
                  )
                },
                swish: {
                  icon: (
                    <PaymentIcon
                      style={{
                        fontSize: 16,
                        marginRight: 4,
                        verticalAlign: "middle"
                      }}
                    />
                  )
                }
              };

              const provider = providerMap[providerName];
              const providerLabel = providerName
                ? t(`common.messages.paymentProviders.${providerName}`)
                : t("common.notAvailable");

              return (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {provider?.icon} {providerLabel}
                </div>
              );
            },
            searchable: true
          },
          {
            title: t("common.tableHeaders.amount"),
            field: "amount",
            cellStyle: {
              padding: isSmallDown ? "8px" : "12px",
              fontWeight: 500
            },
            render: (rowData: any) => <Currency value={rowData.amount} />
          },
          {
            title: t("common.formFields.activationType"),
            field: "mobilePosActivationType",
            cellStyle: {
              padding: isSmallDown ? "8px" : "12px",
              fontWeight: 500
            }
          },
          {
            title: t("common.formFields.integrationStatus"),
            field: "integrationStatus",
            cellStyle: {
              padding: isSmallDown ? "8px" : "12px",
              fontWeight: 500
            },
            render: (rowData: any) => {
              const status = rowData.integrationStatus.toLowerCase();
              const statusLabel = t(`common.integrationStatus.${status}`);
              return <span>{statusLabel}</span>;
            }
          },
          {
            render: (rowData: any) => (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.smallTextButton}
                  startIcon={<GetAppIcon />}
                  onClick={() =>
                    window.open(
                      `/receipts/${rowData.id}?type=${rowData.type}`,
                      "_blank"
                    )
                  }
                  style={{
                    borderRadius: 4,
                    textTransform: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }}
                >
                  {t("common.download")}
                </Button>
              </div>
            ),
            cellStyle: { textAlign: "right" },
            headerStyle: { textAlign: "right" }
          }
        ]}
        data={data}
        detailPanel={rowData => (
          <MobilePOSReceiptItems user={user} receiptId={rowData.id} />
        )}
        options={{
          pageSize: 10,
          headerStyle: {
            backgroundColor: "#f5f5f5",
            color: "#424242",
            fontWeight: "bold",
            padding: "16px 8px"
          },
          rowStyle: rowData => {
            if (rowData.type === "Return") {
              return { backgroundColor: "#ffeeee" };
            }

            return {
              backgroundColor:
                rowData.tableData.id % 2 === 0 ? "#ffffff" : "#f9f9f9"
            };
          },
          searchFieldStyle: {
            borderRadius: 4,
            padding: "8px 12px",
            border: "1px solid #e0e0e0"
          },
          actionsColumnIndex: -1,

          emptyRowsWhenPaging: false,
          pageSizeOptions: [5, 10, 20, 50]
        }}
        localization={{
          toolbar: {
            searchPlaceholder: t("common.search"),
            searchTooltip: t("common.searchTooltip")
          },
          pagination: {
            labelRowsSelect: t("rows"),
            labelDisplayedRows: t("displayedRowsTemplate"),
            firstTooltip: t("first"),
            previousTooltip: t("prev"),
            nextTooltip: t("next"),
            lastTooltip: t("last")
          },
          body: {
            emptyDataSourceMessage: t("noRecords")
          }
        }}
      />
    </div>
  );
};

export default MobilePOSReceipts;
